import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import { organizationIntegrationContract } from "@common/contracts/organization-integration.contract";
import {
    OrganizationConnectionGroupBy,
    OrganizationConnectionItemSort,
} from "@common/schemas/organization-connection-groups.schema";
import {
    OrganizationIntegrationAssignAccountsBatchBody,
    REMIND_BATCH_MAX,
} from "@common/schemas/organization-integration.schema";
import { OrganizationConnectionFilters } from "@common/schemas/organization-connection-filters.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const organizationIntegrationClient = getBackendAPIClient(
    organizationIntegrationContract,
);

const ORG_INTEGRATION_QUERY_PREFIX = "organization-integration";

/**
 * Stable cache-key fragment for the filter set. Arrays are sorted so a different
 * pick order does not create a second cache entry for the same filter.
 *
 * Serialized as JSON rather than delimiter-joined: `search` is free text, so a
 * query containing the delimiter could otherwise collide with a different
 * filter state and serve it a stale cached page.
 */
const filtersCacheKey = ({
    search,
    statuses,
    providers,
}: OrganizationConnectionFilters) =>
    JSON.stringify({
        search: search ?? "",
        statuses: [...(statuses ?? [])].sort((a, b) => a.localeCompare(b)),
        providers: [...(providers ?? [])].sort((a, b) => a.localeCompare(b)),
    });

export const organizationIntegrationQueryKeys = {
    all: (organizationId: string) =>
        [ORG_INTEGRATION_QUERY_PREFIX, organizationId] as const,
    workspaces: (organizationId: string) =>
        [ORG_INTEGRATION_QUERY_PREFIX, organizationId, "workspaces"] as const,
    connections: (organizationId: string, page: number) =>
        [
            ORG_INTEGRATION_QUERY_PREFIX,
            organizationId,
            "connections",
            page,
        ] as const,
    connectionGroups: ({
        organizationId,
        groupBy,
        page,
        filters,
    }: {
        organizationId: string;
        groupBy: OrganizationConnectionGroupBy;
        page: number;
        filters: OrganizationConnectionFilters;
    }) =>
        [
            ORG_INTEGRATION_QUERY_PREFIX,
            organizationId,
            "connection-groups",
            groupBy,
            page,
            filtersCacheKey(filters),
        ] as const,
    connectionGroupItems: ({
        organizationId,
        groupBy,
        groupKey,
        filters,
        sort,
    }: {
        organizationId: string;
        groupBy: OrganizationConnectionGroupBy;
        groupKey: string;
        filters: OrganizationConnectionFilters;
        sort?: OrganizationConnectionItemSort;
    }) =>
        [
            ORG_INTEGRATION_QUERY_PREFIX,
            organizationId,
            "connection-group-items",
            groupBy,
            groupKey,
            filtersCacheKey(filters),
            sort ?? "",
        ] as const,
};

/** Client + entity options for the assignment dropdowns. */
export function useOrgIntegrationWorkspaces(organizationId: string) {
    return useQuery({
        queryKey: organizationIntegrationQueryKeys.workspaces(organizationId),
        queryFn: async () => {
            const response = await organizationIntegrationClient.getWorkspaces({
                params: { organizationId },
            });
            return response.body;
        },
        enabled: Boolean(organizationId),
    });
}

const GROUP_PAGE_SIZE = 10;
export const GROUP_ITEMS_PAGE_SIZE = 10;

/** One page of group buckets for the active grouping dimension. */
export function useOrgConnectionGroups({
    organizationId,
    groupBy,
    page,
    filters = {},
}: {
    organizationId: string;
    groupBy: OrganizationConnectionGroupBy;
    page: number;
    filters?: OrganizationConnectionFilters;
}) {
    const queryKey = organizationIntegrationQueryKeys.connectionGroups({
        organizationId,
        groupBy,
        page,
        filters,
    });

    return useQuery({
        queryKey,
        queryFn: async () => {
            const response =
                await organizationIntegrationClient.getConnectionGroups({
                    params: { organizationId },
                    query: {
                        groupBy,
                        page,
                        limit: GROUP_PAGE_SIZE,
                        ...filters,
                    },
                });
            if (response.status !== 200) {
                throw new Error("Failed to load connection groups");
            }
            return response.body;
        },
        enabled: Boolean(organizationId),
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

/** Pure next-page math for the group-items infinite query (unit-tested). */
export const getNextGroupItemsPageParam = (lastPage: {
    page: number;
    pageCount: number;
}): number | undefined =>
    lastPage.page + 1 < lastPage.pageCount ? lastPage.page + 1 : undefined;

/**
 * Lazily loads a single group's accounts, one page at a time. Enabled only once
 * the group is expanded; `fetchNextPage` backs the "Load more" row.
 */
export function useOrgConnectionGroupItems({
    organizationId,
    groupBy,
    groupKey,
    enabled,
    filters = {},
    sort,
}: {
    organizationId: string;
    groupBy: OrganizationConnectionGroupBy;
    groupKey: string;
    enabled: boolean;
    filters?: OrganizationConnectionFilters;
    sort?: OrganizationConnectionItemSort;
}) {
    return useInfiniteQuery({
        queryKey: organizationIntegrationQueryKeys.connectionGroupItems({
            organizationId,
            groupBy,
            groupKey,
            filters,
            sort,
        }),
        queryFn: async ({ pageParam }) => {
            const response =
                await organizationIntegrationClient.getConnectionGroupItems({
                    params: { organizationId },
                    query: {
                        groupBy,
                        groupKey,
                        page: pageParam,
                        limit: GROUP_ITEMS_PAGE_SIZE,
                        sort,
                        ...filters,
                    },
                });
            if (response.status !== 200) {
                throw new Error("Failed to load group accounts");
            }
            return response.body;
        },
        initialPageParam: 0,
        getNextPageParam: getNextGroupItemsPageParam,
        enabled: enabled && Boolean(organizationId),
    });
}

/** Upper bound the batch-assign endpoint enforces on one request. */
export const ASSIGN_BATCH_MAX = 50;

/**
 * Commits staged client/entity assignments in one request, then refreshes the
 * org integrations data. Covers both first-time assignment and moving an
 * assigned account to another entity of the client it already belongs to;
 * moving it to a different client is rejected.
 *
 * The endpoint validates every assignment before applying any, so a bad
 * assignment fails the whole request with a 4xx (already toasted by the shared
 * axios interceptor). Assignments that pass validation are applied
 * independently, so a 200 can still carry per-account failures in `failed` —
 * callers must inspect it rather than treating 200 as full success.
 */
export function useAssignOrgIntegrationAccountsBatchMutation(
    organizationId: string,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            assignments: OrganizationIntegrationAssignAccountsBatchBody["assignments"],
        ) => {
            const response =
                await organizationIntegrationClient.assignAccountsBatch({
                    params: { organizationId },
                    body: { assignments },
                });
            return response.body;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: organizationIntegrationQueryKeys.all(organizationId),
            });
        },
    });
}

export { REMIND_BATCH_MAX };

/**
 * Asks the people who connected the given disconnected logins to reconnect them.
 *
 * Takes **connection** ids, not account ids: one login is one recipient, so the
 * caller must collapse a row selection down to connections first (see
 * `selectedReminderTargets`).
 *
 * Like assign-batch, a 200 can still carry per-connection failures in `failed`
 * (ineligible row, no email on file, inside the reminder cooldown) — callers
 * must inspect it rather than treating 200 as full success. A rejected request
 * is already toasted by the shared axios interceptor.
 *
 * Deliberately does not invalidate the integrations queries: sending a reminder
 * changes nothing on the table.
 */
export function useRemindOrgIntegrationConnectionsMutation(
    organizationId: string,
) {
    return useMutation({
        mutationFn: async (connectionIds: string[]) => {
            const response =
                await organizationIntegrationClient.remindConnections({
                    params: { organizationId },
                    body: { connectionIds },
                });
            return response.body;
        },
    });
}
